import React from 'react';

// Matches http(s):// and bare www. URLs. Kept intentionally simple; the goal is
// to make pasted links clickable, not to validate every possible URL form.
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;

// Trailing punctuation that is almost always sentence punctuation, not part of
// the URL (e.g. "see https://x.com/foo." — the period isn't in the link).
const TRAILING_PUNCT = /[.,!?;:)\]}'"]+$/;

/**
 * Turns URLs inside a plain-text string into clickable <a> elements, leaving the
 * rest as text. Does NOT use dangerouslySetInnerHTML — the input is user-typed,
 * so everything stays an escaped React text node; only matched URLs become links.
 */
export function linkify(text: string): React.ReactNode {
    if (!text) return text;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    URL_REGEX.lastIndex = 0;

    let key = 0;
    while ((match = URL_REGEX.exec(text)) !== null) {
        const start = match.index;
        let url = match[0];

        // Peel trailing punctuation back out of the link into the text run.
        const trailing = url.match(TRAILING_PUNCT)?.[0] ?? '';
        if (trailing) url = url.slice(0, -trailing.length);

        if (start > lastIndex) parts.push(text.slice(lastIndex, start));

        const href = url.startsWith('www.') ? `https://${url}` : url;
        parts.push(
            <a
                key={key++}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline break-all hover:opacity-80"
                onClick={(e) => e.stopPropagation()}
            >
                {url}
            </a>,
        );
        if (trailing) parts.push(trailing);

        lastIndex = start + match[0].length;
    }

    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
}
